/**
 * Demo-mode fixtures.
 *
 * This file is the *only* thing that needs maintaining as the portal evolves:
 * it maps backend endpoints to canned, realistic-looking JSON. Every screen
 * renders with the real components — they just get fed this data instead of
 * real API responses (see `demoMode.ts`).
 *
 * Endpoints with no entry below fall back to an empty response, so new screens
 * never crash in demo mode — they just show empty states until someone decides
 * to add a fixture here.
 */
import type { InternalAxiosRequestConfig } from 'axios'
import type { User } from '@/models/auth'
import type { Organization } from '@/models/organization'
import type { OrgMember } from '@/models/OrgMember'
import type { Profile } from '@/models/profile'
import type { DailyUsage, UsageData } from '@/models/usage'

export const DEMO_TOKEN = 'demo-mode-token'

const NOW = Date.now()
const DAY = 86_400_000
const ORG_NAME = 'Aurora Logistics'

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export const demoUser: User = {
  id: 'demo-user',
  email: 'sarah.mitchell@auroralogistics.com',
  confirmed_at: '2024-02-01T10:00:00.000Z',
  is_admin: false,
  profiles: [
    mkProfile({
      id: 'demo-user-profile',
      first: 'Demo',
      last: 'User',
      title: 'Sales Manager',
      phone: '+44 7700 900101',
    }),
  ],
}

export const demoOrg = {
  id: 'demo-org',
  name: ORG_NAME,
  official_name: 'Aurora Logistics Ltd',
  business_id: '2891234-5',
  chargebee_id: 'demo-cb-001',
  status: 'approved',
  createdAt: '2024-02-01T10:00:00.000Z',
  inserted_at: '2024-02-01T10:00:00.000Z',
  updatedAt: '2026-05-01T08:30:00.000Z',
  force_org_logo: false,
  my_role: 'admin',
  external_channel_preference: 'sms',
  hubspot_enabled: false,
  pipedrive_enabled: false,
} as unknown as Organization

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

const MEMBER_DEFS = [
  {
    id: 'demo-mem-1',
    first: 'Sarah',
    last: 'Mitchell',
    title: 'Sales Manager',
    role: 'admin',
    phone: '+44 7700 900101',
    scale: 1.45,
  },
  {
    id: 'demo-mem-2',
    first: 'James',
    last: 'Carter',
    title: 'Account Executive',
    role: 'member',
    phone: '+44 7700 900102',
    scale: 1.15,
  },
  {
    id: 'demo-mem-3',
    first: 'Emily',
    last: 'Bennett',
    title: 'Support Lead',
    role: 'admin_only',
    phone: '+44 7700 900103',
    scale: 1.0,
  },
  {
    id: 'demo-mem-4',
    first: 'Michael',
    last: 'Reed',
    title: 'Customer Success',
    role: 'member',
    phone: '+44 7700 900104',
    scale: 0.82,
  },
  {
    id: 'demo-mem-5',
    first: 'Olivia',
    last: 'Hayes',
    title: 'Sales Representative',
    role: 'member',
    phone: '+44 7700 900105',
    scale: 0.66,
  },
  {
    id: 'demo-mem-6',
    first: 'Daniel',
    last: 'Foster',
    title: 'Support Agent',
    role: 'member',
    phone: '+44 7700 900106',
    scale: 0.48,
  },
] as const

function mkProfile(p: {
  id: string
  first: string
  last: string
  title: string
  phone: string
}): Profile {
  return {
    id: p.id,
    first_name: p.first,
    last_name: p.last,
    uuid: p.id,
    name: `${p.first} ${p.last}`,
    username: `${p.first}.${p.last}`.toLowerCase(),
    company: ORG_NAME,
    title: p.title,
    summary: '',
    phone: p.phone,
    phone_number: p.phone,
    phone_country: 'GB',
    picture_url: '',
    active: true,
    location: 'London, United Kingdom',
    name_visibility: 'public',
    company_visibility: 'public',
    title_visibility: 'public',
    summary_visibility: 'public',
    phone_visibility: 'public',
    location_visibility: 'public',
    user_id: `${p.id}-user`,
  }
}

export const demoMembers: OrgMember[] = [
  ...MEMBER_DEFS.map((m) => ({
    id: m.id,
    job_title: m.title,
    user_id: `${m.id}-user`,
    email: `${m.first}.${m.last}@auroralogistics.com`.toLowerCase(),
    role: m.role,
    status: 'accepted' as const,
    joined_at: '2024-02-05T08:00:00.000Z',
    calls_this_month: Math.round(m.scale * 95),
    user: { profile: mkProfile(m) },
  })),
  {
    id: 'demo-mem-7',
    job_title: 'Sales Representative',
    user_id: 'demo-mem-7-user',
    email: 'grace.thompson@auroralogistics.com',
    role: 'member',
    status: 'pending',
    joined_at: new Date(NOW - 3 * DAY).toISOString(),
    calls_this_month: 0,
    user: {
      profile: mkProfile({
        id: 'demo-mem-7',
        first: 'Grace',
        last: 'Thompson',
        title: 'Sales Representative',
        phone: '+44 7700 900107',
      }),
    },
  },
]

// ---------------------------------------------------------------------------
// Usage — generated dynamically so charts populate for any selected date range
// ---------------------------------------------------------------------------

/** Deterministic pseudo-random in [0,1) — stable across re-fetches in a session. */
function rand(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

function dateList(start: Date, end: Date): string[] {
  const days: string[] = []
  const d = new Date(start)
  d.setHours(0, 0, 0, 0)
  const last = new Date(end)
  last.setHours(0, 0, 0, 0)
  let guard = 0
  while (d <= last && guard < 400) {
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    days.push(`${d.getFullYear()}-${m}-${day}`)
    d.setDate(d.getDate() + 1)
    guard++
  }
  return days
}

function memberDaily(dates: string[], mi: number, scale: number): DailyUsage[] {
  return dates.map((date, di) => {
    const dow = new Date(date).getDay()
    const wf = dow === 0 || dow === 6 ? 0.2 : 1
    return {
      date,
      calls_made: Math.round((9 + rand(di * 7 + mi * 131 + 1) * 24) * wf * scale),
      // Kept roughly on par with outside calls (see request: similar volumes).
      sms_sent: Math.round((8 + rand(di * 7 + mi * 131 + 197) * 24) * wf * scale),
    }
  })
}

function totals(daily: DailyUsage[]) {
  return daily.reduce(
    (a, d) => ({
      sms: a.sms + d.sms_sent,
      calls: a.calls + d.calls_made,
    }),
    { sms: 0, calls: 0 },
  )
}

function parseRange(config: InternalAxiosRequestConfig) {
  let start: Date | undefined
  let end: Date | undefined
  try {
    const u = new URL(config.url ?? '')
    const s = u.searchParams.get('start_date')
    const e = u.searchParams.get('end_date')
    if (s) start = new Date(s)
    if (e) end = new Date(e)
  } catch {
    /* fall through to defaults */
  }
  if (!end || isNaN(end.getTime())) end = new Date()
  if (!start || isNaN(start.getTime())) start = new Date(end.getTime() - 30 * DAY)
  return { start, end, startIso: start.toISOString(), endIso: end.toISOString() }
}

function demoUsage(config: InternalAxiosRequestConfig): UsageData {
  const { start, end, startIso, endIso } = parseRange(config)
  const dates = dateList(start, end)
  const perMember = MEMBER_DEFS.map((m, i) => memberDaily(dates, i, m.scale))

  const orgDaily: DailyUsage[] = dates.map((date, di) => ({
    date,
    sms_sent: perMember.reduce((s, m) => s + m[di].sms_sent, 0),
    calls_made: perMember.reduce((s, m) => s + m[di].calls_made, 0),
  }))
  const orgT = totals(orgDaily)

  return {
    organization_id: demoOrg.id,
    organization_name: ORG_NAME,
    total_sms_sent: orgT.sms,
    total_outside_calls_made: orgT.calls,
    daily_usage: orgDaily,
    membership_usages: MEMBER_DEFS.map((m, i) => {
      const t = totals(perMember[i])
      return {
        membership_id: m.id,
        total_sms_sent: t.sms,
        total_outside_calls_made: t.calls,
        daily_usage: perMember[i],
        start_date: startIso,
        end_date: endIso,
      }
    }),
    start_date: startIso,
    end_date: endIso,
  }
}

// ---------------------------------------------------------------------------
// Message stats
// ---------------------------------------------------------------------------

const MSG_DEFS = [
  {
    message: 'Hi! I tried to reach you — please call me back when you have a moment.',
    times_used: 296,
    times_answered: 213,
  },
  {
    message: 'Your delivery is on its way. Could you confirm a good time?',
    times_used: 254,
    times_answered: 190,
  },
  {
    message: 'Quick question about your recent order — give me a call.',
    times_used: 188,
    times_answered: 98,
  },
  {
    message: 'Could you call me back regarding your account?',
    times_used: 142,
    times_answered: 112,
  },
  {
    message: 'Thanks for your business! Reach out any time if you have questions.',
    times_used: 92,
    times_answered: 36,
  },
]

function demoMessageStats(config: InternalAxiosRequestConfig) {
  const { startIso, endIso } = parseRange(config)
  // response_rate is a whole-number percentage (0–100): the UI renders the
  // value followed by a literal "%", so a fraction would show as "0.7%".
  const messages = MSG_DEFS.map((m) => ({
    ...m,
    response_rate: Math.round((m.times_answered / m.times_used) * 100),
  }))
  const total_calls = messages.reduce((s, m) => s + m.times_used, 0)
  const total_answered = messages.reduce((s, m) => s + m.times_answered, 0)
  return {
    organization_id: demoOrg.id,
    messages,
    total_calls,
    total_answered,
    overall_response_rate: Math.round((total_answered / total_calls) * 100),
    start_date: startIso,
    end_date: endIso,
  }
}

// ---------------------------------------------------------------------------
// Call stats — a coherent, flow-conserving Sankey
// ---------------------------------------------------------------------------

function demoCallStats(config: InternalAxiosRequestConfig) {
  const { startIso, endIso } = parseRange(config)
  return {
    organization_id: demoOrg.id,
    start_date: startIso,
    end_date: endIso,
    total_calls: 1200,
    sankey: {
      nodes: [
        { id: 'src_app', stage: 'source', label: 'Mobile app' },
        { id: 'src_web', stage: 'source', label: 'Web portal' },
        { id: 'total', stage: 'total', label: 'All calls' },
        { id: 'reason_sales', stage: 'reason', label: 'Sales' },
        { id: 'reason_support', stage: 'reason', label: 'Support' },
        { id: 'reason_billing', stage: 'reason', label: 'Billing' },
        { id: 'ans_answered', stage: 'answer_status', label: 'Answered' },
        { id: 'ans_missed', stage: 'answer_status', label: 'Missed' },
        { id: 'sent_positive', stage: 'sentiment', label: 'Positive' },
        { id: 'sent_neutral', stage: 'sentiment', label: 'Neutral' },
        { id: 'sent_negative', stage: 'sentiment', label: 'Negative' },
        { id: 'out_resolved', stage: 'outcome', label: 'Resolved' },
        { id: 'out_pending', stage: 'outcome', label: 'Pending' },
        { id: 'fu_yes', stage: 'follow_up', label: 'Follow-up needed' },
        { id: 'fu_no', stage: 'follow_up', label: 'No follow-up' },
      ],
      links: [
        { source: 'src_app', target: 'total', value: 820 },
        { source: 'src_web', target: 'total', value: 380 },
        { source: 'total', target: 'reason_sales', value: 540 },
        { source: 'total', target: 'reason_support', value: 420 },
        { source: 'total', target: 'reason_billing', value: 240 },
        { source: 'reason_sales', target: 'ans_answered', value: 410 },
        { source: 'reason_sales', target: 'ans_missed', value: 130 },
        { source: 'reason_support', target: 'ans_answered', value: 300 },
        { source: 'reason_support', target: 'ans_missed', value: 120 },
        { source: 'reason_billing', target: 'ans_answered', value: 175 },
        { source: 'reason_billing', target: 'ans_missed', value: 65 },
        { source: 'ans_answered', target: 'sent_positive', value: 430 },
        { source: 'ans_answered', target: 'sent_neutral', value: 330 },
        { source: 'ans_answered', target: 'sent_negative', value: 125 },
        { source: 'sent_positive', target: 'out_resolved', value: 400 },
        { source: 'sent_positive', target: 'out_pending', value: 30 },
        { source: 'sent_neutral', target: 'out_resolved', value: 250 },
        { source: 'sent_neutral', target: 'out_pending', value: 80 },
        { source: 'sent_negative', target: 'out_resolved', value: 55 },
        { source: 'sent_negative', target: 'out_pending', value: 70 },
        { source: 'out_resolved', target: 'fu_no', value: 620 },
        { source: 'out_resolved', target: 'fu_yes', value: 85 },
        { source: 'out_pending', target: 'fu_yes', value: 150 },
        { source: 'out_pending', target: 'fu_no', value: 30 },
      ],
    },
  }
}

// ---------------------------------------------------------------------------
// Subscription & billing
// ---------------------------------------------------------------------------

const demoBundle = {
  id: 'demo-bundle-001',
  invoice_id: 'INV-2026-0105',
  total_messages: 5000,
  used_messages: 2840,
  remaining_messages: 2160,
  status: 'active',
  auto_replenish: true,
  replenish_threshold: 500,
  inserted_at: new Date(NOW - 12 * DAY).toISOString(),
}

const demoSubscription = {
  id: 'demo-sub-001',
  status: 'active',
  seats: 25,
  plan_id: 'koll-business-monthly',
  plan_quantity: 25,
  subscription_items: [
    { item_price_id: 'koll-business-monthly', quantity: 25 },
    { item_price_id: 'sms-bundle-5000', quantity: 1 },
  ],
  current_term_start: Math.floor((NOW - 12 * DAY) / 1000),
  current_term_end: Math.floor((NOW + 18 * DAY) / 1000),
  next_billing_at: Math.floor((NOW + 18 * DAY) / 1000),
  billing_period: 1,
  billing_period_unit: 'month',
  currency_code: 'EUR',
  mrr: 47500,
  customer: {
    first_name: 'Sarah',
    last_name: 'Mitchell',
    email: 'billing@auroralogistics.com',
    company: 'Aurora Logistics Ltd',
  },
  card: { last4: '4242', card_type: 'visa', expiry_month: 8, expiry_year: 2027 },
}

const demoInvoices = [0, 1, 2, 3].map((i) => ({
  id: `INV-2026-${String(105 - i).padStart(4, '0')}`,
  subscription_id: 'demo-sub-001',
  amount: 47500,
  currency: 'EUR',
  status: i === 0 ? 'payment_due' : 'paid',
  issued_at: Math.floor((NOW - i * 30 * DAY) / 1000),
  line_items: [
    { id: `li-${i}-1`, description: 'Koll Business — 25 seats', amount: 37500, quantity: 25 },
    { id: `li-${i}-2`, description: 'SMS bundle (5 000 messages)', amount: 10000, quantity: 1 },
  ],
}))

// ---------------------------------------------------------------------------
// Call reason templates — kept mutable so the Settings editor feels live
// ---------------------------------------------------------------------------

let demoTemplates = [
  {
    id: 1,
    content: 'Hi! I tried to reach you. Please call me back when you can.',
    position: 1,
    active: true,
  },
  {
    id: 2,
    content: 'Your delivery is on its way — could you confirm a good time?',
    position: 2,
    active: true,
  },
  {
    id: 3,
    content: 'I have a couple of questions about your order, call me when it suits you.',
    position: 3,
    active: true,
  },
  {
    id: 4,
    content: 'Thanks for getting in touch! I will get back to you shortly.',
    position: 4,
    active: false,
  },
]
let nextTemplateId = 5

// ---------------------------------------------------------------------------
// Mutation helpers — demo writes don't persist, but they shouldn't look broken
// ---------------------------------------------------------------------------

function parseBody(config: InternalAxiosRequestConfig): Record<string, unknown> {
  try {
    if (typeof config.data === 'string') return JSON.parse(config.data)
    if (config.data && typeof config.data === 'object')
      return config.data as Record<string, unknown>
  } catch {
    /* ignore */
  }
  return {}
}

function lastSegment(path: string): string {
  return path.split('/').filter(Boolean).pop() ?? ''
}

// ---------------------------------------------------------------------------
// Route table
// ---------------------------------------------------------------------------

type Handler = (config: InternalAxiosRequestConfig, path: string) => unknown

interface Route {
  method: string
  pattern: RegExp
  handler: Handler
}

const routes: Route[] = [
  // Auth
  { method: 'post', pattern: /\/portal\/me$/, handler: () => ({ user: demoUser }) },
  {
    method: 'post',
    pattern: /\/portal\/login\/?$/,
    handler: () => ({ token: DEMO_TOKEN, user: demoUser }),
  },
  {
    method: 'post',
    pattern: /\/portal\/register$/,
    handler: () => ({
      token: DEMO_TOKEN,
      user: {
        id: demoUser.id,
        email: demoUser.email,
        confirmed_at: demoUser.confirmed_at,
        first_name: 'Sarah',
        last_name: 'Mitchell',
      },
    }),
  },
  { method: 'delete', pattern: /\/portal\/logout$/, handler: () => ({}) },

  // Organization
  { method: 'get', pattern: /\/organizations\/current$/, handler: () => ({ data: [demoOrg] }) },
  {
    method: 'get',
    pattern: /\/organizations\/admin_invites$/,
    handler: () => ({ admin_invites: [] }),
  },
  { method: 'get', pattern: /\/organizations$/, handler: () => ({ data: [demoOrg] }) },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/members$/,
    handler: () => ({ data: demoMembers }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/remaining-messages$/,
    handler: () => ({ remaining_messages: demoBundle.remaining_messages }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/message-bundles$/,
    handler: () => ({ data: `${demoBundle.remaining_messages} messages remaining` }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/hubspot\/status$/,
    handler: () => ({ connected: false }),
  },
  {
    method: 'get',
    pattern: /\/integrations\/pipedrive\/connected-members$/,
    handler: () => ({ data: [] }),
  },
  // Calling systems: show the showcase org calling through 3CX, with Aircall
  // available but unconnected, so both states of the card are on display.
  {
    method: 'get',
    pattern: /\/aircall\/connection$/,
    handler: () => ({ data: { connected: false, webhook_active: false } }),
  },
  {
    method: 'get',
    pattern: /\/threecx\/connection$/,
    handler: () => ({
      data: {
        connected: true,
        webhook_active: true,
        webhook_url: 'https://api.koll.app/api/threecx/webhook?token=demo-token',
        webhook_token: 'demo-token',
        web_client_url: 'https://demo.3cx.cloud',
      },
    }),
  },
  { method: 'get', pattern: /\/profiles$/, handler: () => ({ data: [] }) },

  // Usage & statistics
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/usage$/,
    handler: (c) => ({ data: demoUsage(c) }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/message-stats$/,
    handler: (c) => ({ data: demoMessageStats(c) }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/call-stats$/,
    handler: (c) => ({ data: demoCallStats(c) }),
  },

  // Subscription
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/active-bundle$/,
    handler: () => ({ data: demoBundle }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/subscription$/,
    handler: () => ({ data: demoSubscription }),
  },
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/invoices$/,
    handler: () => ({ data: demoInvoices }),
  },

  // Call reason templates — full in-memory CRUD so Settings feels real
  {
    method: 'get',
    pattern: /\/organizations\/[^/]+\/call-reason-templates$/,
    handler: () => ({ data: demoTemplates }),
  },
  {
    method: 'post',
    pattern: /\/organizations\/[^/]+\/call-reason-templates\/reorder$/,
    handler: () => ({}),
  },
  {
    method: 'post',
    pattern: /\/organizations\/[^/]+\/call-reason-templates$/,
    handler: (c) => {
      const body = parseBody(c)
      const tpl = {
        id: nextTemplateId++,
        content: String(body.content ?? body.text ?? 'New call reason'),
        position: demoTemplates.length + 1,
        active: true,
      }
      demoTemplates.push(tpl)
      return { data: tpl }
    },
  },
  {
    method: 'patch',
    pattern: /\/organizations\/[^/]+\/call-reason-templates\/[^/]+$/,
    handler: (c, path) => {
      const id = Number(lastSegment(path))
      const body = parseBody(c)
      const crt = body.call_reason_template as Record<string, unknown> | undefined
      const content = crt?.text ?? body.content
      const tpl = demoTemplates.find((t) => t.id === id)
      if (tpl && content != null) tpl.content = String(content)
      return { data: tpl ?? {} }
    },
  },
  {
    method: 'delete',
    pattern: /\/organizations\/[^/]+\/call-reason-templates\/[^/]+$/,
    handler: (_c, path) => {
      const id = Number(lastSegment(path))
      demoTemplates = demoTemplates.filter((t) => t.id !== id)
      return {}
    },
  },

  // Organization update — merge the change so toggles/edits visibly "stick"
  {
    method: 'patch',
    pattern: /\/organizations\/[^/]+$/,
    handler: (c) => {
      Object.assign(demoOrg, parseBody(c))
      return { data: demoOrg }
    },
  },
]

/**
 * Look up canned data for a request. Unmatched endpoints get a harmless empty
 * response so demo mode degrades gracefully instead of crashing.
 */
export function resolveFixture(
  method: string,
  path: string,
  config: InternalAxiosRequestConfig,
): unknown {
  for (const route of routes) {
    if (route.method === method && route.pattern.test(path)) {
      return route.handler(config, path)
    }
  }
  console.debug(`[demo] no fixture for ${method.toUpperCase()} ${path} — returning empty`)
  return method === 'get' ? { data: [] } : { data: {} }
}
